import React from "react";
import { Link } from "react-router-dom";
import { EnvelopeSimple, Phone, MapPin, WhatsappLogo, ShieldCheck } from "@phosphor-icons/react";
import { SITE, whatsappLink } from "@/data/site";
import { INDIA_CITIES, KOREA_CITIES } from "@/data/cities";
import { SERVICE_LIST } from "@/data/services";
import { TRUST_BADGES } from "@/data/trust";

const footerLinks = {
  Company: [
    { label: "Send to South Korea", to: "/send-to-south-korea-from-india" },
    { label: "Customs Guide", to: "/south-korea-customs-guide" },
    { label: "What You Can Send", to: "/what-you-can-send-to-south-korea" },
    { label: "About Us", to: "/about" },
    { label: "Contact", to: "/contact" },
    { label: "Trust & Safety", to: "/trust" },
    { label: "Pricing", to: "/pricing" },
    { label: "Why us vs EMS", to: "/why-shipkorea-vs-ems" },
    { label: "Track Shipment", to: "https://www.shipoms.in/track" },
    { label: "Blog", to: "/blog" },
    { label: "FAQ", to: "/faq" },
  ],
  Services: SERVICE_LIST.slice(0, 6).map((s) => ({ label: s.heading.replace(" (India ↔ Korea)", ""), to: `/${s.slug}` })),
  Legal: [
    { label: "Privacy Policy", to: "/privacy-policy" },
    { label: "Terms & Conditions", to: "/terms" },
    { label: "Refund Policy", to: "/refund-policy" },
  ],
};

export function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-slate-950 text-slate-300 mt-24">
      {/* CTA band */}
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="relative -translate-y-16 rounded-3xl bg-korea-blue overflow-hidden px-8 py-12 md:px-14 md:py-16 shadow-2xl shadow-korea-blue/30">
          <div className="absolute inset-0 hero-grid-bg opacity-20" aria-hidden />
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="text-blue-200 text-xs font-bold uppercase tracking-[0.2em] mb-3">
                {SITE.tagline}
              </p>
              <h2 className="text-white text-3xl md:text-4xl font-display font-bold tracking-tight max-w-xl">
                Ready to send your parcel to South Korea?
              </h2>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="https://www.shipoms.in/book"
                data-testid="footer-cta-book"
                className="inline-flex items-center rounded-full bg-white text-korea-blue px-7 py-3.5 font-semibold hover:bg-blue-50 transition-colors"
              >
                Book Free Pickup
              </Link>
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noreferrer nofollow"
                data-testid="footer-cta-whatsapp"
                className="inline-flex items-center gap-2 rounded-full bg-korea-red text-white px-7 py-3.5 font-semibold hover:bg-korea-redhover transition-colors"
              >
                <WhatsappLogo weight="fill" className="h-5 w-5" /> WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 pb-12 -mt-6">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
          <div className="col-span-2">
            <Link to="/" data-testid="footer-logo" className="flex items-center gap-2.5 mb-4">
              <img src={SITE.logoBox} alt="ShipKorea.in logo" loading="lazy" decoding="async" className="h-10 w-auto object-contain" />
              <span className="font-display font-extrabold text-xl tracking-tight leading-none">
                <span className="text-white">Ship</span>
                <span className="text-korea-red">Korea</span>
                <span className="text-amber-500">.in</span>
              </span>
            </Link>
            <p className="text-sm text-slate-400 leading-relaxed max-w-xs">
              India&apos;s trusted courier service to South Korea. Fast, secure, door-to-door delivery
              with free pickup, DDP shipping and customs clearance.
            </p>
            <div className="mt-5 space-y-2 text-sm">
              <a href={`tel:${SITE.phone}`} className="flex items-center gap-2 hover:text-white">
                <Phone className="h-4 w-4 text-korea-red" /> {SITE.phoneDisplay}
              </a>
              <a href={`mailto:${SITE.email}`} className="flex items-center gap-2 hover:text-white">
                <EnvelopeSimple className="h-4 w-4 text-korea-red" /> {SITE.email}
              </a>
              <p className="flex items-start gap-2 text-slate-400">
                <MapPin className="h-4 w-4 text-korea-red mt-0.5" /> {SITE.address}
              </p>
            </div>
          </div>

          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title}>
              <h4 className="text-white font-semibold text-sm mb-4">{title}</h4>
              <ul className="space-y-2.5">
                {links.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-sm text-slate-400 hover:text-white transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div>
            <h4 className="text-white font-semibold text-sm mb-4">Korea Coverage</h4>
            <ul className="space-y-2.5">
              {KOREA_CITIES.slice(0, 6).map((c) => (
                <li key={c.slug}>
                  <Link to={`/courier-to-korea/${c.slug}`} className="text-sm text-slate-400 hover:text-white transition-colors">
                    Courier to {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Trust badges strip */}
        <div className="mt-10 pt-8 border-t border-white/10" data-testid="footer-trust-strip">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">
            Registered · Insured · Verifiable
          </p>
          <div className="flex flex-wrap gap-2.5">
            {TRUST_BADGES.map((b) => (
              <span
                key={b.title}
                className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-xs text-slate-300"
              >
                <ShieldCheck weight="fill" className="h-3.5 w-3.5 text-emerald-400" />
                {b.title}
              </span>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-500 leading-relaxed max-w-2xl">
            {SITE.legalName} · Registered office: {SITE.address} · Working hours: {SITE.hours}. 5-day delivery guarantee — or ₹500 back. Free ₹15,000 insurance on every shipment.
            <Link to="/trust" className="ml-1 text-korea-red hover:text-white transition-colors">See verification →</Link>
          </p>
        </div>

        {/* City ribbon */}
        <div className="mt-10 pt-8 border-t border-white/10">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
            Courier from Indian cities to South Korea
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {INDIA_CITIES.map((c) => (
              <Link
                key={c.slug}
                to={`/courier-from-india/${c.slug}`}
                className="text-xs text-slate-500 hover:text-korea-red transition-colors"
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-3 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} {SITE.name}. All rights reserved.</p>
          <p>Dedicated India–South Korea Logistics Specialist</p>
        </div>
      </div>
    </footer>
  );
}
